// src/routes/AppRoutes.js
import React, { useContext, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { ROLES } from '../constants/roles'; // Importa roles

// Importa componentes principales y de estado
import Login from '../components/auth/Login';
import AwaitRole from '../components/common/AwaitRole';
import Unauthorized from '../components/common/Unauthorized';
import NotFound from '../components/common/NotFound';

// Importa las rutas del dashboard (que ahora usan lazy loading internamente)
import DashboardRoutes from './DashboardRoutes';

// Importa un componente de carga (Spinner)
import { CircularProgress, Box } from '@mui/material';

// Componente simple para mostrar mientras carga el contexto o componentes lazy
const LoadingFallback = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
    <CircularProgress />
  </Box>
);

const AppRoutes = () => {
  // Obtiene estado del contexto de usuario
  const { currentUser, userRole, loading } = useContext(UserContext);

  // 1. Muestra el fallback si el contexto de usuario aún está cargando
  if (loading) {
    return <LoadingFallback />;
  }

  // 2. Rutas para usuarios NO autenticados
  if (!currentUser) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        {/* Redirección raíz para usuarios no autenticados */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        {/* Cualquier otra ruta redirige a login */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // 3. Rutas para usuarios autenticados PERO con rol PENDIENTE
  if (userRole === ROLES.PENDIENTE) {
    return (
      <Routes>
        <Route path="/awaitRole" element={<AwaitRole />} />
        {/* Protege el acceso a login si ya está en proceso de asignación */}
        <Route path="/login" element={<Navigate to="/awaitRole" replace />} />
        {/* Cualquier otra ruta redirige a awaitRole */}
        <Route path="*" element={<Navigate to="/awaitRole" replace />} />
      </Routes>
    );
  }

  // 4. Rutas para usuarios autenticados Y con rol asignado
  return (
    // Suspense para manejar la carga diferida de los componentes de ruta
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Rutas públicas o de estado específicas */}
        <Route path="/awaitRole" element={<Navigate to="/dashboard" replace />} />
        <Route path="/unauthorized" element={<Unauthorized />} />

        {/* Redirige desde /login si ya está autenticado y con rol */}
        <Route path="/login" element={<Navigate to="/dashboard" replace />} />

        {/* Define la estructura principal del Dashboard */}
        {/* Renderiza directamente las rutas del dashboard */}
        {DashboardRoutes.map((route) => {
          // Normalizar el path: si ya empieza con /, usarlo tal cual; si no, agregarlo
          const normalizedPath = route.path.startsWith('/') ? route.path : `/${route.path}`;
          return (
            <Route key={route.path} path={normalizedPath} element={route.element} />
          );
        })}
        {/* Redirección base - debe ser la última para que no interfiera con otras rutas */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* Catch-all final para rutas no encontradas */}
        <Route path="*" element={<NotFound />} />

      </Routes>
    </Suspense>
  );
};

export default AppRoutes;